import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PaymentInstructionLayout from "../layouts/PaymentInstructionLayout";
import { authorizedFetch, SessionExpiredError } from "../../utils/authorizedFetch";
import { PaymentLinkErrorCode, getErrorMessage } from "../../utils/paymentLinkErrorHandler";
import useTranslation from "../hooks/useTranslation";

const BASE_URL = process.env.REACT_APP_API_URL;

export default function HostedCardCheckout({
  amount,
  orderId,
  merchantName,
  expired_at,
  selected_method,
  onCheckStatus,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const customer = useSelector((state) => state.customer);
  const paymentLinkToken = useSelector((state) => state.payment.token);
  const [fullName, setFullName] = useState(customer?.fullName || "");
  const [email, setEmail] = useState(customer?.email || "");
  const [phone, setPhone] = useState(customer?.phone || "");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    if (!fullName.trim() || !email.trim() || !phone.trim()) {
      setError(t("paymentInstructionCard.errorRequired"));
      return;
    }

    setLoading(true);
    try {
      const response = await authorizedFetch(`${BASE_URL}/api/payment-link/asteripay-checkout`, {
        dispatch,
        token: paymentLinkToken,
        refresh: { kind: "history", reffNo: orderId },
        method: "POST",
        body: JSON.stringify({
          reff_no: orderId,
          full_name: fullName.trim(),
          email: email.trim(),
          phone: phone.trim(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.handoff_url) {
        setError(data.message || t("paymentInstructionCard.errorPaymentFailed"));
        return;
      }
      window.location.assign(data.handoff_url);
    } catch (err) {
      setError(
        err instanceof SessionExpiredError
          ? getErrorMessage(PaymentLinkErrorCode.SESSION_LOST)
          : err.message || t("paymentInstructionCard.errorPaymentRequestFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PaymentInstructionLayout
      amount={amount}
      orderId={orderId}
      merchantName={merchantName}
      expiredAt={expired_at}
      selectedMethod={selected_method}
      onCheckStatus={onCheckStatus}
      paymentCredential={
        <form className="mx-auto mt-4 max-w-md space-y-4" onSubmit={submit}>
          <p className="text-center text-gray-600">{t("paymentInstructionCard.hostedIntro")}</p>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("paymentInstructionCard.cardholderNameLabel")}</span>
            <input
              className="w-full rounded-lg border px-3 py-2"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              autoComplete="name"
              required
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("paymentInstructionCard.emailLabel")}</span>
            <input
              className="w-full rounded-lg border px-3 py-2"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("paymentInstructionCard.phoneLabel")}</span>
            <input
              className="w-full rounded-lg border px-3 py-2"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              autoComplete="tel"
              required
            />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-black py-3 font-semibold text-white disabled:opacity-60"
          >
            {loading ? t("paymentInstructionCard.processingCardPayment") : t("paymentInstructionCard.continueToCard")}
          </button>
        </form>
      }
    />
  );
}
